export const formatDocument = (document) => {
    if (!document) return '';

    // Remove all non-numeric characters
    const numbers = document.replace(/\D/g, '');

    // CPF format: 000.000.000-00 (CPF nunca tem letra, então continua
    // detectado só pelos dígitos)
    if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    // CNPJ (numérico ou alfanumérico — Nota Técnica 2026.001 da Receita
    // Federal): os 12 primeiros caracteres podem ser letra OU dígito, os 2
    // últimos (DV) são sempre numéricos. Um CNPJ 100% numérico cai no mesmo
    // resultado de antes.
    const alphanumeric = document.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    if (alphanumeric.length === 14) {
        return alphanumeric.replace(/([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return document;
};

// CNPJ alfanumérico (Nota Técnica 2026.001 da Receita Federal, vale para
// CNPJs emitidos a partir de jul/2026): os 12 primeiros caracteres (raiz +
// filial) podem ser letra maiúscula OU dígito; os 2 dígitos verificadores
// (DV) continuam sempre numéricos.
const CNPJ_BODY_LENGTH = 12;
const CNPJ_DV1_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_DV2_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

// Valor de cada caractere = código ASCII - 48 (ex.: '0'→0, '9'→9, 'A'→17,
// 'Z'→42). Para dígitos '0'-'9' isso é igual ao próprio valor numérico, o
// que preserva o cálculo do DV para CNPJs 100% numéricos já cadastrados.
const charValue = (char) => char.charCodeAt(0) - 48;

const calculateCNPJCheckDigit = (base, weights) => {
    const sum = base
        .split('')
        .reduce((acc, char, index) => acc + charValue(char) * weights[index], 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
};

// Valida um CNPJ (com ou sem máscara), aceitando tanto o formato numérico
// tradicional quanto o novo alfanumérico.
export const isValidCNPJ = (value) => {
    if (!value) return false;

    const cleaned = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();

    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cleaned)) return false;

    const base = cleaned.slice(0, CNPJ_BODY_LENGTH);
    const dv1 = calculateCNPJCheckDigit(base, CNPJ_DV1_WEIGHTS);
    const dv2 = calculateCNPJCheckDigit(base + String(dv1), CNPJ_DV2_WEIGHTS);

    return cleaned === `${base}${dv1}${dv2}`;
};

// Máscara do campo de CNPJ enquanto o usuário digita — reconstrói o valor
// formatado a cada tecla a partir dos caracteres já digitados, igual à
// técnica de `formatPhone`/`formatCurrency` (neste arquivo) e de
// `handleLicensePlateChange` (`pages/vehicles/Form.js`), o único outro campo
// do app que mistura letra e número. Evitamos aplicar `react-input-mask`
// (usado no restante deste app só para máscaras 100% numéricas) num padrão
// alfanumérico — o formatChar `*` da lib combinado com força de maiúsculas
// via `beforeMaskedValueChange` se mostrou não confiável para esse caso,
// descartando os caracteres digitados e impedindo o CNPJ de ficar completo.
export const formatCNPJInput = (rawValue) => {
    if (!rawValue) return '';

    // Todos os caracteres alfanuméricos já digitados (sem a pontuação da
    // máscara), em maiúsculas.
    const alnum = rawValue.toUpperCase().replace(/[^0-9A-Z]/g, '');

    // 12 primeiros caracteres (raiz + filial): letra maiúscula OU dígito.
    const body = alnum.slice(0, 12);
    // 2 últimos caracteres (dígitos verificadores): sempre numéricos — o que
    // sobrar de `alnum` depois do corpo, restrito a dígitos.
    const dv = alnum.slice(12).replace(/[^0-9]/g, '').slice(0, 2);

    let formatted = body.slice(0, 2);
    if (body.length > 2) formatted += `.${body.slice(2, 5)}`;
    if (body.length > 5) formatted += `.${body.slice(5, 8)}`;
    if (body.length > 8) formatted += `/${body.slice(8, 12)}`;
    if (dv.length > 0) formatted += `-${dv}`;

    return formatted;
};

export const formatPhone = (phone) => {
    if (!phone) return '';

    // Remove all non-numeric characters
    const numbers = phone.replace(/\D/g, '');

    // Format based on length
    if (numbers.length === 11) {
        // Mobile format: (00) 00000-0000
        return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (numbers.length === 10) {
        // Landline format: (00) 0000-0000
        return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    return phone;
};

export const formatCurrency = (value) => {
    ;
    let v = value.replace(/\D/g, '');
    v = (v / 100).toFixed(2) + '';
    v = v.replace('.', ',');
    v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    // Remove zeros à esquerda
    v = v.replace(/^0+(?=\d)/, '');
    return v;
};